<script setup>
import axios from 'axios'
import { router } from '@inertiajs/vue3'
import { useToast } from 'primevue/usetoast'
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

/**
 * RED-387: `Proovi katsearuande peal` beside `Salvesta uus versioon`.
 *
 * Sends the draft on screen (saved or not) to `admin.report-qc.trials.store`;
 * the server queues one engine run on REdata's invented report. While it runs
 * the button shows `Proovin…` and the page asks for the result every few
 * seconds; a trial that is already running when the page opens is picked up
 * again. The result line (D3) says what the trial found, and the current
 * version's range when one is known. A result that belongs to another draft
 * than the one on screen says so. Nothing here writes to the editor's draft.
 *
 * RED-361 D4: under the result, the server's verdict in plain words (same as
 * the current version: you may save; clearly worse: do not; nothing to compare).
 * D2: `Näita vigade kaupa` lists every planted error (found, not detected, or
 * not asked), whether the current version found it, the question that did,
 * and the false alarms; the texts come from the server. D3: the invented
 * report itself, to read.
 */
const props = defineProps({
  // The payload: any of `items`, `queries`, `frame` (a part left out = today's live state).
  draft: { type: Function, required: true },
  // The hash of the draft on screen, or null when it is not known yet.
  draftHash: { type: String, default: null },
  disabled: { type: Boolean, default: false },
  trial: { type: Object, required: true },
})

const { t } = useI18n()
const toast = useToast()

const latest = ref(props.trial.latest)
const starting = ref(false)
let timer = null

const running = computed(() => ['queued', 'running'].includes(latest.value?.status))

watch(
  () => props.trial.latest,
  value => {
    if (!running.value) {
      latest.value = value
    }
  },
)

const usdFormat = new Intl.NumberFormat('et-EE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

const result = computed(() => {
  const trial = latest.value
  if (trial?.status !== 'done') {
    return null
  }
  return t('messages.admin.report_qc.trial.result', {
    found: trial.found,
    ceiling: trial.ceiling,
    falseAlarms: trial.trapHits,
    usd: usdFormat.format(trial.usd ?? 0),
  })
})

const forThisDraft = computed(() => latest.value !== null && props.draftHash !== null && latest.value?.hash === props.draftHash)
const range = computed(() => props.trial.baselineRange)
const rangeText = computed(() => (range.value ? (range.value.min === range.value.max ? `${range.value.min}` : `${range.value.min}-${range.value.max}`) : null))
// Only for the draft on screen: a verdict on another draft says nothing about saving this one.
const verdict = computed(() => (result.value && forThisDraft.value ? (latest.value?.verdict ?? null) : null))

const errorsOpen = ref(false)
// Null for a trial from before the per-error result was stored: the list shows "—".
const errors = computed(() => latest.value?.errors ?? null)

const stateStyle = {
  found: { icon: 'pi pi-check', class: 'text-green-700' },
  missed: { icon: 'pi pi-times', class: 'text-red-700' },
  not_asked: { icon: 'pi pi-minus', class: 'text-gray-500' },
}

const verdictStyle = {
  same: { icon: 'pi pi-check-circle', class: 'border-green-200 bg-green-50 text-green-800' },
  worse: { icon: 'pi pi-exclamation-triangle', class: 'border-amber-300 bg-amber-50 text-amber-900' },
  unknown: { icon: 'pi pi-info-circle', class: 'border-gray-200 bg-gray-50 text-gray-700' },
}

const hint = computed(() => t('messages.admin.report_qc.trial.button_hint', { usd: usdFormat.format(props.trial.typicalUsd ?? 0) }))

const finished = trial => {
  if (trial.status === 'done') {
    toast.add({
      severity: 'success',
      summary: t('messages.admin.report_qc.trial.done_title'),
      detail: t('messages.admin.report_qc.trial.done_detail', { found: trial.found, expected: trial.ceiling, falseAlarms: trial.trapHits }),
      life: 8000,
    })
  } else {
    toast.add({ severity: 'error', summary: t('messages.admin.report_qc.trial.failed_title'), detail: trial.error, life: 10000 })
  }

  // The history's `Proovimata` badges and the range follow the new result.
  router.reload({ only: ['checklistVersions', 'trial'] })
}

const poll = () => {
  clearTimeout(timer)
  timer = setTimeout(async () => {
    try {
      const response = await axios.get(route('admin.report-qc.trials.show', latest.value.id))
      latest.value = response.data.trial
    } catch {
      // A missed answer is asked again on the next tick.
    }

    if (running.value) {
      poll()
    } else {
      finished(latest.value)
    }
  }, (props.trial.pollSeconds ?? 5) * 1000)
}

const start = async () => {
  starting.value = true

  try {
    const response = await axios.post(route('admin.report-qc.trials.store'), props.draft())
    latest.value = response.data.trial

    if (running.value) {
      poll()
    } else {
      // The sync queue (tests, e2e) answers with the finished trial.
      finished(latest.value)
    }
  } catch (error) {
    const data = error?.response?.data ?? {}

    if (data.title) {
      toast.add({ severity: 'warn', summary: data.title, detail: data.detail, life: 8000 })
    } else {
      const message = data.errors ? Object.values(data.errors).flat()[0] : null
      toast.add({ severity: 'error', summary: t('messages.admin.report_qc.trial.failed_title'), detail: message ?? t('messages.admin.report_qc.trial.start_error'), life: 8000 })
    }
  } finally {
    starting.value = false
  }
}

onMounted(() => {
  if (running.value) {
    poll()
  }
})

onBeforeUnmount(() => clearTimeout(timer))
</script>

<template>
  <div class="flex flex-wrap items-center justify-end gap-2" data-testid="checklist-trial">
    <p v-if="result" class="w-full text-right text-sm" data-testid="checklist-trial-result">
      <span v-if="!forThisDraft" class="text-gray-500" data-testid="checklist-trial-previous">{{ t('messages.admin.report_qc.trial.previous') }} </span>
      {{ result }}
      <span v-if="rangeText" data-testid="checklist-trial-baseline">{{ t('messages.admin.report_qc.trial.baseline', { range: rangeText }) }}</span>
    </p>
    <p
      v-if="verdict"
      class="flex w-full items-start gap-2 rounded-md border px-3 py-2 text-left text-sm sm:ml-auto sm:max-w-xl"
      :class="verdictStyle[verdict.level].class"
      :data-testid="`checklist-trial-verdict-${verdict.level}`"
    >
      <i :class="verdictStyle[verdict.level].icon" class="mt-0.5" aria-hidden="true" />
      <span>{{ t(`messages.admin.report_qc.trial.verdict_${verdict.level}`, { n: verdict.diff }) }}</span>
    </p>
    <div v-if="result" class="flex w-full flex-wrap items-center justify-end gap-x-4 gap-y-1 text-sm">
      <Button
        :label="t(errorsOpen ? 'messages.admin.report_qc.trial.errors_hide' : 'messages.admin.report_qc.trial.errors_show')"
        :icon="errorsOpen ? 'pi pi-chevron-down' : 'pi pi-chevron-right'"
        link
        size="small"
        class="w-auto! p-0!"
        :aria-expanded="errorsOpen"
        data-testid="checklist-trial-errors-toggle"
        @click="errorsOpen = !errorsOpen"
      />
      <a :href="route('admin.report-qc.trials.report')" class="text-blue-600 underline underline-offset-2 hover:text-blue-800" data-testid="checklist-trial-report">
        <i class="pi pi-download mr-1 text-xs" aria-hidden="true" />{{ t('messages.admin.report_qc.trial.report_download') }}
      </a>
    </div>
    <div v-if="result && errorsOpen" class="w-full text-left text-sm" data-testid="checklist-trial-errors">
      <p v-if="!errors" class="text-gray-500" data-testid="checklist-trial-errors-none">—</p>
      <template v-else>
        <p class="mb-2 text-xs text-gray-500">{{ t('messages.admin.report_qc.trial.errors_hint') }}</p>
        <div class="overflow-x-auto rounded-md border border-gray-200">
          <table class="w-full min-w-[40rem] text-sm">
            <thead class="bg-gray-50 text-left text-xs text-gray-600">
              <tr>
                <th class="px-2 py-1 font-medium">{{ t('messages.admin.report_qc.trial.col_error') }}</th>
                <th class="px-2 py-1 font-medium">{{ t('messages.admin.report_qc.trial.col_items') }}</th>
                <th class="px-2 py-1 font-medium">{{ t('messages.admin.report_qc.trial.col_severity') }}</th>
                <th class="px-2 py-1 font-medium">{{ t('messages.admin.report_qc.trial.col_this') }}</th>
                <th class="px-2 py-1 font-medium">{{ t('messages.admin.report_qc.trial.col_current') }}</th>
                <th class="px-2 py-1 font-medium">{{ t('messages.admin.report_qc.trial.col_by') }}</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-100">
              <tr v-for="row in errors.defects" :key="row.id" :data-testid="`checklist-trial-error-${row.id}`" :data-state="row.state">
                <td class="px-2 py-1"><span class="mr-1 font-mono text-xs text-gray-500">{{ row.id }}</span>{{ row.what }}</td>
                <td class="px-2 py-1 font-mono text-xs whitespace-nowrap">{{ row.items.join(', ') }}</td>
                <td class="px-2 py-1 text-xs whitespace-nowrap">{{ row.severity }}</td>
                <td class="px-2 py-1 whitespace-nowrap" :class="stateStyle[row.state].class">
                  <i :class="stateStyle[row.state].icon" class="mr-1 text-xs" aria-hidden="true" />{{ t(`messages.admin.report_qc.trial.state_${row.state}`) }}
                </td>
                <td class="px-2 py-1 whitespace-nowrap">
                  <template v-if="row.current === null">—</template>
                  <i
                    v-else
                    :class="[stateStyle[row.current].icon, stateStyle[row.current].class]"
                    :aria-label="t(`messages.admin.report_qc.trial.state_${row.current}`)"
                    v-tooltip.top="t(`messages.admin.report_qc.trial.state_${row.current}`)"
                  />
                </td>
                <td class="px-2 py-1 font-mono text-xs">{{ row.by.join(', ') || '—' }}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p class="mt-3 font-medium" data-testid="checklist-trial-traps-title">{{ t('messages.admin.report_qc.trial.traps_title', { n: errors.traps.length }) }}</p>
        <ul v-if="errors.traps.length" class="mt-1 flex flex-col gap-1">
          <li v-for="trap in errors.traps" :key="trap.id" :data-testid="`checklist-trial-trap-${trap.id}`">
            <span class="mr-1 font-mono text-xs text-gray-500">{{ trap.id }}</span>{{ trap.what }}
          </li>
        </ul>
        <p v-else class="mt-1 text-gray-600">{{ t('messages.admin.report_qc.trial.traps_none') }}</p>
      </template>
    </div>
    <p v-else-if="latest?.status === 'failed' && latest.error" class="w-full text-right text-sm text-red-600" data-testid="checklist-trial-error">
      {{ latest.error }}
    </p>
    <Button
      v-tooltip.top="{ value: hint, showDelay: 300, pt: { root: { style: { maxWidth: '360px' } } } }"
      :label="running ? t('messages.admin.report_qc.trial.running') : t('messages.admin.report_qc.trial.button')"
      icon="pi pi-play"
      severity="secondary"
      outlined
      class="w-auto!"
      :loading="running || starting"
      :disabled="disabled || running || starting"
      data-testid="checklist-trial-start"
      @click="start"
    />
  </div>
</template>
